'use client'
// AI generated

import { Add } from '@carbon/icons-react'
import type {
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  XYPosition,
} from '@xyflow/react'
import {
  addEdge,
  applyEdgeChanges,
  Background,
  ConnectionMode,
  Controls,
  Panel,
  ReactFlow,
} from '@xyflow/react'
import { Button } from 'app/_components/Button'
import type { DragEvent } from 'react'
import { useCallback, useRef } from 'react'
import { useReadOnly } from 'slate-react'
import '@xyflow/react/dist/style.css'
import type { FlowElement } from '..'
import {
  CANVAS_BACKGROUND,
  DEFAULT_BORDER_COLOR,
  DEFAULT_NODE_BACKGROUND,
  GROUP_SIZE,
} from '../constants'
import { useFlowState } from '../hooks/useFlowState'
import { useHelperLines } from '../hooks/useHelperLines'
import { useNodeGrouping } from '../hooks/useNodeGrouping'
import { applyChanges, hasChanges } from '../lib/changes'
import CustomEdge from './CustomEdge'
import { CustomNode } from './CustomNode'
import GroupNode from './GroupNode'
import HelperLines from './HelperLines'

const nodeTypes = {
  custom: CustomNode,
  group: GroupNode,
}

const edgeTypes = {
  custom: CustomEdge,
}

const onDragOver = (event: DragEvent) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
}

const proOptions = {
  account: 'paid-pro',
  hideAttribution: true,
}

const canvasStyle = { backgroundColor: CANVAS_BACKGROUND }

export default function FlowCanvas({ element }: { element: FlowElement }) {
  const readonly = useReadOnly()
  const isInteractive = !readonly
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null)

  const { nodes, edges, setNodes, setEdges, rfInstance, setRfInstance } =
    useFlowState(element)
  const { horizontal, vertical, snapToHelperLines } = useHelperLines()
  const onNodeDragStop = useNodeGrouping(setNodes)

  const updateNodes = useCallback(
    (changes: NodeChange<Node>[]) => {
      // Validate changes (dimension change is fired every frame)
      if (!hasChanges(changes, nodes)) return

      setNodes(applyChanges(snapToHelperLines(changes, nodes), nodes))
    },
    [nodes, setNodes, snapToHelperLines],
  )

  const updateEdges = useCallback(
    (changes: EdgeChange<Edge>[]) => setEdges(applyEdgeChanges(changes, edges)),
    [edges, setEdges],
  )

  const onConnect = useCallback(
    (params: Connection) =>
      setEdges(addEdge({ ...params, type: 'custom' }, edges)),
    [edges, setEdges],
  )

  const getFlowCenterPosition = useCallback(
    (offset: XYPosition) => {
      const bounds = reactFlowWrapper.current?.getBoundingClientRect()
      const center = bounds
        ? {
            x: bounds.left + bounds.width / 2 - offset.x,
            y: bounds.top + bounds.height / 2 - offset.y,
          }
        : { x: 0, y: 0 }
      const position = rfInstance?.screenToFlowPosition(center) ?? center
      const rand = {
        x: (Math.random() - 0.5) * 20,
        y: (Math.random() - 0.5) * 20,
      }

      return {
        x: position.x + rand.x,
        y: position.y + rand.y,
      }
    },
    [rfInstance],
  )

  const onAdd = useCallback(() => {
    const position = getFlowCenterPosition({ x: 90, y: 12 })

    const newNode: Node = {
      id: `node_${+new Date()}`,
      type: 'custom',
      data: {
        label: 'Neuer Node',
        backgroundColor: DEFAULT_NODE_BACKGROUND,
        borderColor: DEFAULT_BORDER_COLOR,
      },
      position,
    }

    setNodes(nodes.concat(newNode))
  }, [getFlowCenterPosition, nodes, setNodes])

  const onAddGroup = useCallback(() => {
    const position = getFlowCenterPosition({
      x: GROUP_SIZE.width / 2,
      y: GROUP_SIZE.height / 2,
    })
    const newNode = {
      id: `node_${+new Date()}`,
      type: 'group',
      data: {
        label: 'Neue Gruppe',
        backgroundColor: DEFAULT_NODE_BACKGROUND,
      },
      position,
      ...GROUP_SIZE,
      measured: {
        ...GROUP_SIZE,
      },
    }

    setNodes(nodes.concat(newNode))
  }, [getFlowCenterPosition, nodes, setNodes])

  return (
    <ReactFlow
      ref={reactFlowWrapper}
      nodes={nodes}
      edges={edges}
      onNodesChange={readonly ? undefined : updateNodes}
      onEdgesChange={readonly ? undefined : updateEdges}
      onConnect={onConnect}
      onNodeDragStop={onNodeDragStop}
      onDragOver={onDragOver}
      onInit={setRfInstance}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      style={canvasStyle}
      panOnDrag={isInteractive}
      nodesDraggable={isInteractive}
      nodesConnectable={isInteractive}
      zoomOnScroll={isInteractive}
      panOnScroll={isInteractive}
      proOptions={proOptions}
      connectionMode={ConnectionMode.Loose}
    >
      {isInteractive && <Background />}
      <Controls showInteractive={false} />
      {isInteractive && (
        <>
          <HelperLines horizontal={horizontal} vertical={vertical} />
          <Panel position="top-right" className="flex flex-row gap-0.5">
            <Button onClick={onAdd}>
              Neuer Node <Add className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button onClick={onAddGroup}>
              Neue Gruppe <Add className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Panel>
        </>
      )}
    </ReactFlow>
  )
}
